import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, AlertIcon, Badge, Box, Button, ButtonGroup, Flex, Grid, HStack, Input, InputGroup, InputLeftAddon,
  InputLeftElement, Select, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack, Wrap, WrapItem, useMediaQuery,
} from "@chakra-ui/react";
import { RefreshCw, Search, SlidersHorizontal, X } from "lucide-react";
import { getSaudeAnunciosAcoes, getSaudeAnunciosExecucoes } from "../../services/api";
import DetalheItem from "./DetalheItem";
import {
  AZUL, BadgeResultado, CarregandoSobre, EXECUCAO, ErroCarga, Paginacao, Vazio, dataHoraBR, duracao, nomeConta, num,
  resumoExecucao, rotulo, rotuloAcao, usd, useCelular,
} from "./comum";

const POR_PAGINA = 30;
const TAMANHO_CAMPO = { base: "md", lg: "sm" };
const TAMANHO_ACAO = { base: "md", lg: "sm" };
const FILTROS_PADRAO = { conta: "", acao: "", resultado: "", busca: "", de: "", ate: "" };
const INTERVALO_EXECUCOES = 30000;

/** Lista com o padrão das outras: contador descarta resposta atrasada e a de depois de sair da aba. */
function useListaPaginada(buscar, params) {
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const ultima = useRef(0);

  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    const minha = ++ultima.current;
    if (!silencioso) setCarregando(true);
    const r = await buscar({ ...params, pagina, por_pagina: POR_PAGINA });
    if (minha !== ultima.current) return;
    setCarregando(false);
    if (r) {
      setDados(r);
      setErro(false);
    } else {
      setErro(true);
    }
  }, [buscar, params, pagina]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { ultima.current += 1; }, []);

  return { pagina, setPagina, dados, carregando, erro, carregar };
}

function LinhaAnuncio({ a, nomesContas }) {
  return (
    <>
      {a.id_ml || "Sem anúncio"}
      <Text as="span" fontSize="xs" color="gray.600" fontWeight="normal">
        {" "}· {nomeConta(nomesContas, a.conta_id)}{a.produto_id ? ` · produto #${a.produto_id}` : ""}
      </Text>
    </>
  );
}

/** Resumo da execução com rótulos; chave desconhecida fica atrás de "Ver tudo". */
function ResumoExecucao({ resumo, rotulos }) {
  const [tudo, setTudo] = useState(false);
  const r = resumoExecucao(resumo, rotulos);
  const linha = (lista) => lista.map((x) => `${x.rotulo}: ${x.valor}`).join(" · ");
  if (!r.principais.length && !r.porTipo.length && !r.porCura.length && !r.outros.length) {
    return <Text fontSize="xs" color="gray.600">—</Text>;
  }
  return (
    <Box fontSize="xs" color="gray.700" wordBreak="break-word">
      {r.principais.length > 0 && <Text>{linha(r.principais)}</Text>}
      {r.porTipo.length > 0 && <Text mt={0.5}><Text as="span" fontWeight="semibold">Por tipo: </Text>{linha(r.porTipo)}</Text>}
      {r.porCura.length > 0 && <Text mt={0.5}><Text as="span" fontWeight="semibold">Por cura: </Text>{linha(r.porCura)}</Text>}
      {r.outros.length > 0 && (
        <>
          <Button size="xs" variant="link" colorScheme="blue" minH={{ base: "40px", lg: "auto" }} mt={{ base: 0, md: 1 }}
            onClick={() => setTudo((v) => !v)} aria-expanded={tudo}>
            {tudo ? "Esconder" : `Ver tudo (${r.outros.length})`}
          </Button>
          {tudo && <Text mt={0.5} color="gray.600">{linha(r.outros)}</Text>}
        </>
      )}
    </Box>
  );
}

function ListaAcoes({ rotulos, opcoes, config, isAdmin, onMudou }) {
  const celular = useCelular();
  // A dica longa não cabe ao lado do botão "Filtros" abaixo de 360 px (400 px com "Filtros (n)").
  const [largura360, largura400] = useMediaQuery(["(min-width: 360px)", "(min-width: 400px)"], { ssr: false });
  const [filtros, setFiltros] = useState(FILTROS_PADRAO);
  const [busca, setBusca] = useState("");
  const [aberto, setAberto] = useState(null);
  const [verFiltros, setVerFiltros] = useState(false);
  const topoLista = useRef(null);

  const params = useMemo(() => ({
    conta: filtros.conta || undefined,
    acao: filtros.acao || undefined,
    resultado: filtros.resultado || undefined,
    busca: filtros.busca || undefined,
    de: filtros.de || undefined,
    ate: filtros.ate || undefined,
  }), [filtros]);

  const lista = useListaPaginada(getSaudeAnunciosAcoes, params);
  const { setPagina } = lista;

  // Busca com pausa na digitação; filtro novo sempre volta para a página 1.
  useEffect(() => {
    const termo = busca.trim();
    if (termo === filtros.busca) return undefined;
    const t = setTimeout(() => {
      setFiltros((f) => ({ ...f, busca: termo }));
      setPagina(1);
    }, 400);
    return () => clearTimeout(t);
  }, [busca, filtros.busca, setPagina]);

  const mudar = (campo, valor) => {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPagina(1);
  };
  const filtrado = Object.keys(FILTROS_PADRAO).some((k) => filtros[k] !== FILTROS_PADRAO[k]);
  const qtdFiltros = ["conta", "acao", "resultado", "de", "ate"].filter((k) => filtros[k]).length;
  const dicaBusca = (qtdFiltros ? largura400 : largura360) ? "MLB ou produto" : "Buscar…";
  const limpar = () => {
    setBusca("");
    setFiltros(FILTROS_PADRAO);
    setPagina(1);
  };

  const { dados, carregando, erro, pagina, carregar } = lista;
  const itens = dados?.itens || [];
  const total = dados?.total || 0;
  const porPagina = dados?.por_pagina || POR_PAGINA;
  const abrir = (a) => { if (a.item_id) setAberto({ id: a.item_id, id_ml: a.id_ml, conta_id: a.conta_id, produto_id: a.produto_id }); };

  return (
    <>
      {/* No celular os filtros ficam recolhidos atrás do botão "Filtros". */}
      <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(3, minmax(0, 1fr))", xl: "minmax(0, 1.4fr) repeat(5, minmax(0, 1fr))" }}
        gap={2} mb={2}>
        <Flex gap={2} minW={0}>
          <InputGroup size={TAMANHO_CAMPO} flex="1" minW={0}>
            <InputLeftElement pointerEvents="none"><Search size={14} color="#718096" aria-hidden /></InputLeftElement>
            <Input type="search" bg="white" placeholder={dicaBusca} aria-label="Buscar no histórico"
              value={busca} onChange={(e) => setBusca(e.target.value)} />
          </InputGroup>
          <Button display={{ base: "inline-flex", md: "none" }} flexShrink={0} variant="outline" bg="white"
            leftIcon={<SlidersHorizontal size={16} />} onClick={() => setVerFiltros((v) => !v)}
            aria-expanded={verFiltros} aria-controls="saude-historico-filtros">
            Filtros{qtdFiltros ? ` (${qtdFiltros})` : ""}
          </Button>
        </Flex>
        <Box id="saude-historico-filtros" display={{ base: verFiltros ? "contents" : "none", md: "contents" }}>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Conta" value={filtros.conta} onChange={(e) => mudar("conta", e.target.value)}>
            <option value="">Todas as contas</option>
            {opcoes.contas.map((c) => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
          </Select>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Resultado" value={filtros.resultado} onChange={(e) => mudar("resultado", e.target.value)}>
            <option value="">Todos os resultados</option>
            {opcoes.resultados.map((r) => <option key={r} value={r}>{rotulo(rotulos, "resultados", r)}</option>)}
          </Select>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Ação" value={filtros.acao} onChange={(e) => mudar("acao", e.target.value)}>
            <option value="">Todas as ações</option>
            {opcoes.acoes.map((a) => <option key={a} value={a}>{rotuloAcao(rotulos, a)}</option>)}
          </Select>
          <InputGroup size={TAMANHO_CAMPO}>
            <InputLeftAddon px={2} fontSize="xs">De</InputLeftAddon>
            <Input type="date" bg="white" aria-label="De (data)" value={filtros.de} max={filtros.ate || undefined}
              onChange={(e) => mudar("de", e.target.value)} px={2} />
          </InputGroup>
          <InputGroup size={TAMANHO_CAMPO}>
            <InputLeftAddon px={2} fontSize="xs">Até</InputLeftAddon>
            <Input type="date" bg="white" aria-label="Até (data)" value={filtros.ate} min={filtros.de || undefined}
              onChange={(e) => mudar("ate", e.target.value)} px={2} />
          </InputGroup>
        </Box>
      </Grid>

      <Flex justify="space-between" align="center" mb={2} minH="28px" gap={2} flexWrap="wrap">
        <Text fontSize="xs" color="gray.600" aria-live="polite">{dados ? `${num(total)} registro${total === 1 ? "" : "s"}` : ""}</Text>
        <HStack spacing={3}>
          {filtrado && (
            <Button size={TAMANHO_ACAO} variant="ghost" colorScheme="blue" leftIcon={<X size={12} />} onClick={limpar}>Limpar filtros</Button>
          )}
          <Button size={TAMANHO_ACAO} variant="ghost" leftIcon={<RefreshCw size={14} />} onClick={() => carregar()} isLoading={carregando && !!dados}>
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {erro && dados && (
        <Alert status="warning" mb={3} borderRadius="md" fontSize="sm"><AlertIcon />Não foi possível atualizar. Mostrando a última leitura.</Alert>
      )}

      {erro && !dados ? (
        <ErroCarga titulo="Não foi possível carregar o histórico." onTentar={() => carregar()} carregando={carregando} />
      ) : (
        <Box ref={topoLista} scrollMarginTop="80px" bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100"
          position="relative" minH="120px" aria-busy={carregando}>
          {carregando && <CarregandoSobre />}
          {!carregando && dados && itens.length === 0 && (
            <Vazio titulo={filtrado ? "Nada com esses filtros" : "Nada registrado ainda"}>
              {filtrado
                ? "Mude o período ou tire algum filtro."
                : "Aqui aparece cada diagnóstico, correção, recriação e verificação — também o que seria feito em simulação."}
            </Vazio>
          )}

          {itens.length > 0 && celular && (
            <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
              {itens.map((a) => {
                const clicavel = !!a.item_id;
                return (
                  <Box key={a.id} p={3} {...(clicavel ? {
                    role: "button", tabIndex: 0, cursor: "pointer", _hover: { bg: "gray.50" }, _active: { bg: "gray.100" },
                    _focusVisible: { boxShadow: "outline", outline: "none" }, onClick: () => abrir(a),
                    onKeyDown: (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); abrir(a); } },
                  } : {})}>
                    <Flex justify="space-between" align="flex-start" gap={2}>
                      <Wrap spacing={1.5}>
                        <WrapItem><Badge variant="subtle" fontSize="0.7em">{rotuloAcao(rotulos, a.acao)}</Badge></WrapItem>
                        <WrapItem><BadgeResultado resultado={a.resultado} rotulos={rotulos} /></WrapItem>
                        {a.simulacao && a.resultado !== "simulado" && (
                          <WrapItem><Badge colorScheme="purple" variant="outline" fontSize="0.7em">simulação</Badge></WrapItem>
                        )}
                      </Wrap>
                      <Text fontSize="xs" color="gray.600" flexShrink={0}>{dataHoraBR(a.created_at)}</Text>
                    </Flex>
                    <Text fontSize="sm" fontWeight="bold" mt={1}><LinhaAnuncio a={a} nomesContas={opcoes.nomesContas} /></Text>
                    {a.resumo && <Text fontSize="xs" color="gray.700" wordBreak="break-word">{a.resumo}</Text>}
                    {a.erro && (
                      <Text fontSize="xs" color="red.600" mt={1} noOfLines={3} wordBreak="break-word">
                        {a.http_code ? `HTTP ${a.http_code}: ` : ""}{a.erro}
                      </Text>
                    )}
                    <Text fontSize="2xs" color="gray.600" mt={1}>
                      {a.usuario_nome || "automático"}{Number(a.custo_ia_usd) > 0 ? ` · IA ${usd(a.custo_ia_usd)}` : ""}
                    </Text>
                  </Box>
                );
              })}
            </VStack>
          )}

          {itens.length > 0 && !celular && (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr><Th>Quando</Th><Th>Ação</Th><Th>Anúncio</Th><Th>O que aconteceu</Th><Th isNumeric>IA</Th><Th>Quem</Th></Tr>
                </Thead>
                <Tbody>
                  {itens.map((a) => (
                    <Tr key={a.id} _hover={a.item_id ? { bg: "gray.50" } : undefined} cursor={a.item_id ? "pointer" : undefined}
                      onClick={() => abrir(a)}>
                      <Td whiteSpace="nowrap" fontSize="xs">{dataHoraBR(a.created_at)}</Td>
                      <Td>
                        <VStack align="flex-start" spacing={1}>
                          <Badge variant="subtle" fontSize="0.7em">{rotuloAcao(rotulos, a.acao)}</Badge>
                          <HStack spacing={1}>
                            <BadgeResultado resultado={a.resultado} rotulos={rotulos} />
                            {a.simulacao && a.resultado !== "simulado" && <Badge colorScheme="purple" variant="outline" fontSize="0.7em">simulação</Badge>}
                          </HStack>
                        </VStack>
                      </Td>
                      <Td>
                        {a.item_id ? (
                          <Button variant="link" size="sm" colorScheme="blue" fontWeight="bold" onClick={(e) => { e.stopPropagation(); abrir(a); }}>
                            {a.id_ml || "Sem anúncio"}
                          </Button>
                        ) : <Text fontSize="sm" fontWeight="bold">{a.id_ml || "—"}</Text>}
                        <Text fontSize="2xs" color="gray.600">
                          {nomeConta(opcoes.nomesContas, a.conta_id)}{a.produto_id ? ` · produto #${a.produto_id}` : ""}
                        </Text>
                      </Td>
                      <Td maxW="440px">
                        <Text fontSize="xs">{a.resumo || "—"}</Text>
                        {a.erro && (
                          <Text fontSize="xs" color="red.600" noOfLines={3} wordBreak="break-word">
                            {a.http_code ? `HTTP ${a.http_code}: ` : ""}{a.erro}
                          </Text>
                        )}
                      </Td>
                      <Td isNumeric fontSize="xs" whiteSpace="nowrap">{Number(a.custo_ia_usd) > 0 ? usd(a.custo_ia_usd) : "—"}</Td>
                      <Td fontSize="xs">{a.usuario_nome || "automático"}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      <Paginacao pagina={pagina} porPagina={porPagina} total={total} carregando={carregando} onPagina={setPagina}
        rolarPara={topoLista} />

      <DetalheItem item={aberto} rotulos={rotulos} config={config} isAdmin={isAdmin} nomesContas={opcoes.nomesContas}
        onFechar={() => setAberto(null)} onMudou={() => { carregar({ silencioso: true }); onMudou?.(); }} />
    </>
  );
}

const SEM_PARAMS = {};

const statusExecucao = (e) => EXECUCAO.status[e.status] || { label: e.status || "—", cor: "gray" };
const titulo = (e) => EXECUCAO.tipo[e.tipo] || e.tipo || "—";

function BadgeStatus({ e }) {
  const s = statusExecucao(e);
  return (
    <Badge colorScheme={s.cor} fontSize="0.7em" display="inline-flex" alignItems="center" gap={1} flexShrink={0}>
      {e.status === "em_andamento" && <Spinner size="xs" color={AZUL} speed="0.8s" />}
      {s.label}
    </Badge>
  );
}

function ListaExecucoes({ rotulos, nomesContas }) {
  const celular = useCelular();
  const topoLista = useRef(null);
  const lista = useListaPaginada(getSaudeAnunciosExecucoes, SEM_PARAMS);
  const { dados, carregando, erro, pagina, setPagina, carregar } = lista;
  const itens = dados?.itens || [];
  const total = dados?.total || 0;
  const porPagina = dados?.por_pagina || POR_PAGINA;
  const rodando = itens.some((e) => e.status === "em_andamento");

  // Com execução em andamento, acompanha sozinho (só com a página visível).
  useEffect(() => {
    if (!rodando) return undefined;
    const atualizar = () => { if (!document.hidden) carregar({ silencioso: true }); };
    const id = setInterval(atualizar, INTERVALO_EXECUCOES);
    document.addEventListener("visibilitychange", atualizar);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", atualizar);
    };
  }, [rodando, carregar]);

  return (
    <>
      <Flex justify="space-between" align="center" mb={2} minH="28px" gap={2} flexWrap="wrap">
        <Text fontSize="xs" color="gray.600">
          Varreduras e ciclos de correção.{rodando ? " Atualiza sozinho enquanto houver uma em andamento." : ""}
        </Text>
        <Button size={TAMANHO_ACAO} variant="ghost" leftIcon={<RefreshCw size={14} />} onClick={() => carregar()} isLoading={carregando && !!dados}>
          Atualizar
        </Button>
      </Flex>

      {erro && dados && (
        <Alert status="warning" mb={3} borderRadius="md" fontSize="sm"><AlertIcon />Não foi possível atualizar. Mostrando a última leitura.</Alert>
      )}

      {erro && !dados ? (
        <ErroCarga titulo="Não foi possível carregar as execuções." onTentar={() => carregar()} carregando={carregando} />
      ) : (
        <Box ref={topoLista} scrollMarginTop="80px" bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100"
          position="relative" minH="120px" aria-busy={carregando}>
          {carregando && <CarregandoSobre />}
          {!carregando && dados && itens.length === 0 && (
            <Vazio titulo="Nenhuma execução ainda">
              A primeira aparece quando rodar uma varredura: pelo “Varrer agora” ou, com o sistema ligado, a automática do dia.
            </Vazio>
          )}

          {itens.length > 0 && celular && (
            <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
              {itens.map((e) => (
                  <Box key={e.id} p={3}>
                    <Flex justify="space-between" align="flex-start" gap={2}>
                      <Wrap spacing={1.5}>
                        <WrapItem><Text fontSize="sm" fontWeight="bold">{titulo(e)}</Text></WrapItem>
                        <WrapItem><Badge variant="outline" color="gray.700" fontSize="0.7em">{EXECUCAO.origem[e.origem] || e.origem || "—"}</Badge></WrapItem>
                        {e.simulacao && <WrapItem><Badge colorScheme="purple" variant="outline" fontSize="0.7em">simulação</Badge></WrapItem>}
                      </Wrap>
                      <BadgeStatus e={e} />
                    </Flex>
                    <Text fontSize="xs" color="gray.700" mt={1}>
                      {nomeConta(nomesContas, e.conta_id)} · {dataHoraBR(e.iniciado_em)} · {duracao(e.iniciado_em, e.finalizado_em)}
                    </Text>
                    <Box mt={1}><ResumoExecucao resumo={e.resumo} rotulos={rotulos} /></Box>
                    {e.erro && <Text fontSize="xs" color="red.600" mt={1} noOfLines={3} wordBreak="break-word">{e.erro}</Text>}
                    <Text fontSize="2xs" color="gray.600" mt={1}>{e.usuario_nome || "automático"}</Text>
                  </Box>
              ))}
            </VStack>
          )}

          {itens.length > 0 && !celular && (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr><Th>Início</Th><Th>Execução</Th><Th>Status</Th><Th>Duração</Th><Th>Resumo</Th><Th>Quem</Th></Tr>
                </Thead>
                <Tbody>
                  {itens.map((e) => (
                      <Tr key={e.id}>
                        <Td whiteSpace="nowrap" fontSize="xs">{dataHoraBR(e.iniciado_em)}</Td>
                        <Td>
                          <Text fontSize="sm" fontWeight="semibold">{titulo(e)}</Text>
                          <Text fontSize="2xs" color="gray.600">
                            {nomeConta(nomesContas, e.conta_id)} · {EXECUCAO.origem[e.origem] || e.origem || "—"}{e.simulacao ? " · simulação" : ""}
                          </Text>
                        </Td>
                        <Td><BadgeStatus e={e} /></Td>
                        <Td fontSize="xs" whiteSpace="nowrap">{duracao(e.iniciado_em, e.finalizado_em)}</Td>
                        <Td maxW="460px">
                          <ResumoExecucao resumo={e.resumo} rotulos={rotulos} />
                          {e.erro && <Text fontSize="xs" color="red.600" noOfLines={3} wordBreak="break-word">{e.erro}</Text>}
                        </Td>
                        <Td fontSize="xs">{e.usuario_nome || "automático"}</Td>
                      </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      <Paginacao pagina={pagina} porPagina={porPagina} total={total} carregando={carregando} onPagina={setPagina}
        rolarPara={topoLista} />
    </>
  );
}

export default function HistoricoTab({ rotulos, opcoes, config, isAdmin, onMudou }) {
  const [vista, setVista] = useState("acoes");
  return (
    <Box p={{ base: 3, md: 4 }}>
      <ButtonGroup size={{ base: "md", lg: "sm" }} isAttached variant="outline" mb={3} role="group" aria-label="O que mostrar"
        w={{ base: "100%", sm: "auto" }}>
        {[{ id: "acoes", label: "Ações" }, { id: "execucoes", label: "Execuções" }].map((v) => (
          <Button key={v.id} flex={{ base: 1, sm: "none" }} aria-pressed={vista === v.id} onClick={() => setVista(v.id)}
            bg={vista === v.id ? "orange.500" : "white"} color={vista === v.id ? "white" : "gray.700"}
            borderColor={vista === v.id ? "orange.500" : "gray.200"} _hover={{ bg: vista === v.id ? "orange.600" : "gray.50" }}>
            {v.label}
          </Button>
        ))}
      </ButtonGroup>
      {/* Ações ficam montadas (só escondidas) para não perder filtros e página ao olhar as execuções. */}
      <Box display={vista === "acoes" ? "block" : "none"}>
        <ListaAcoes rotulos={rotulos} opcoes={opcoes} config={config} isAdmin={isAdmin} onMudou={onMudou} />
      </Box>
      {vista === "execucoes" && <ListaExecucoes rotulos={rotulos} nomesContas={opcoes.nomesContas} />}
    </Box>
  );
}
